import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbArrowRight, TbGift } from "solid-icons/tb";
import { type Component, createSignal, For, onMount, Show } from "solid-js";
import { isServer } from "solid-js/web";
import { webDays } from "virtual:changelog-whats-new";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";

// Seen-state is local, per device, like the desktop banner's dismissal: a second browser
// shows the dot once more, which was accepted over server-side state.
const SEEN_KEY = "chief-whats-new-seen";
const DAYS_SHOWN = 2;

const today = () => new Date().toISOString().slice(0, 10);
const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const days = webDays.slice(0, DAYS_SHOWN);

/**
 * The gift icon in the sidebar footer: a dot when a web entry is newer than the last one
 * seen on this device, and a popover listing the newest deploy-days with each section as
 * a link to its anchor on the entry page. Opening it is what marks the entries seen.
 */
export const WhatsNewPopover: Component<{ placement: "top" | "right"; buttonClass: string }> = (props) => {
  const { t } = useI18n();
  const [unseen, setUnseen] = createSignal(false);
  const [open, setOpen] = createSignal(false);

  onMount(() => {
    if (isServer) return;
    const seen = localStorage.getItem(SEEN_KEY);
    // A first-ever session has nothing "new" to show, so it stamps today silently. Deploys
    // as such never light the dot; only an entry dated after the stamp does.
    if (!seen) localStorage.setItem(SEEN_KEY, today());
    else setUnseen(days.some((day) => day.date > seen));
  });

  const onOpenChange = (next: boolean) => {
    if (next) trackFeature({ feature: "nav.whats_new", via: "button" });
    setOpen(next);
    if (next && days.length > 0) {
      localStorage.setItem(SEEN_KEY, days[0].date);
      setUnseen(false);
    }
  };

  const row = "flex items-center gap-2 rounded px-1 py-1 text-[13px] leading-snug no-underline hover:bg-canvas";

  return (
    <StPopover
      noArrow
      slide
      flip
      gutter={8}
      placement={props.placement === "top" ? "top-start" : "right-end"}
      open={open()}
      onOpenChange={onOpenChange}
      portalProps={{ mount: isServer ? undefined : (document.getElementById("portal") ?? document.body) }}
      contentProps={{ class: "z-[120] w-[22rem] max-h-[80vh] overflow-y-auto p-0" }}
      content={
        <div class="flex flex-col px-4 pt-3.5 pb-3">
          <h4 class="mb-1 text-sm font-bold text-primary-text">
            {t("whats_new.title")} · {days.length}
          </h4>
          <For each={days}>
            {(day, index) => {
              const href = (anchor?: string) => `/changelog/${day.slug}${anchor ? `#${anchor}` : ""}`;
              return (
                <div class="flex flex-col gap-0.5 border-t border-primary-border py-2.5 first:border-t-0">
                  <Show when={index() === 0 && day.hero}>
                    <a href={href()} class="mb-1.5 block overflow-hidden rounded-md border border-primary-border">
                      <img src={day.hero} alt={day.title} loading="lazy" class="block w-full" />
                    </a>
                  </Show>
                  <div class="flex items-baseline justify-between gap-2.5">
                    <a
                      href={href()}
                      class="text-[13.5px] leading-tight font-bold text-primary-text no-underline hover:text-brand hover:underline"
                    >
                      {day.title}
                    </a>
                    <time datetime={day.date} class="shrink-0 text-[11.5px] text-secondary-text tabular-nums">
                      {shortDate(day.date)}
                    </time>
                  </div>
                  <For each={day.sections}>
                    {(section) => (
                      <a href={href(section.slug)} class={`${row} text-primary-text hover:text-brand`}>
                        <span class="size-1.5 shrink-0 rounded-full bg-brand/75" aria-hidden />
                        {section.text}
                        <TbArrowRight class="ml-auto size-3.5 shrink-0 text-brand/60" aria-hidden />
                      </a>
                    )}
                  </For>
                  <Show when={day.fixes > 0}>
                    <a href={href("fixes")} class={`${row} text-secondary-text hover:text-brand`}>
                      <span class="size-1.5 shrink-0 rounded-full bg-secondary-text/60" aria-hidden />
                      {day.fixes === 1 ? t("whats_new.fixes_one") : t("whats_new.fixes", { count: String(day.fixes) })}
                      <TbArrowRight class="ml-auto size-3.5 shrink-0 text-brand/60" aria-hidden />
                    </a>
                  </Show>
                </div>
              );
            }}
          </For>
          <a
            href="/changelog"
            class="mt-1 border-t border-primary-border pt-2.5 text-[12.5px] font-semibold text-brand no-underline hover:underline"
          >
            {t("whats_new.see_all")} →
          </a>
        </div>
      }
    >
      <StTooltip content={t("nav_latest_changes")} placement={props.placement}>
        <StTooltipTrigger as="div" class="flex">
          <StPopoverTrigger as="button" type="button" class={props.buttonClass} aria-label={t("nav_latest_changes")}>
            <span class="relative flex">
              <TbGift class="size-5 text-secondary-text" aria-hidden />
              <Show when={unseen()}>
                <span
                  aria-hidden
                  class="absolute -top-1 -right-1 size-2 rounded-full border-2 border-panel bg-rose-600"
                />
              </Show>
            </span>
          </StPopoverTrigger>
        </StTooltipTrigger>
      </StTooltip>
    </StPopover>
  );
};
